import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../appStore';
import { IPageFlowElementModel, SurveyStep } from '../../models';
import { ActionCreators as CulturesActionCreators } from '../../store/CulturesStore';
import { ActionCreators as SurveyBuilderActionCreators } from '../../store/SurveyBuilderStore';
import { loadSurveyDimensions } from '../../store/SurveyDimensionsSlice';
import { ActionCreators as SurveyActionCreators } from '../../store/SurveyStore';

export const useBranchingAndLayout = () => {
	const dispatch = useAppDispatch();
	const params = useParams<{ surveyId: string }>();
	const surveyId = +params.surveyId;

	const { availableCultures } = useAppSelector((state) => state.cultures);
	const { questions } = useAppSelector((state) => state.survey.surveyEditModel);
	const loadCultures = availableCultures.length === 0;

	useEffect(() => {
		if (loadCultures) {
			dispatch(CulturesActionCreators.GetCultures());
		}
	}, [loadCultures, dispatch]);

	useEffect(() => {
		dispatch(loadSurveyDimensions(surveyId));
	}, [dispatch, surveyId]);

	const { surveyEditModel, isLoading } = useAppSelector((state) => state.survey);
	const { hasUnsavedChanges } = useAppSelector((state) => state.surveyBuilder);

	const [isExpanded, setIsExpanded] = useState(false);
	const [isSorting, setIsSorting] = useState(false);
	const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
	const [pageFlowElement, setPageFlowElement] = useState<IPageFlowElementModel | undefined>(undefined);

	const handlePageFlowElementModalClose = () => setPageFlowElement(undefined);

	const handleWarningModalClose = () => setIsWarningModalOpen(false);

	const handleWarningModalSubmit = () => {
		setIsWarningModalOpen(false);
		dispatch(SurveyActionCreators.GetSurvey(surveyId));
	};

	const handleSortingChange = (itemIds: string[], isFirstPageRandomized: boolean) => {
		const ids = itemIds.map((id) => parseInt(id, 10));
		dispatch(SurveyActionCreators.SortSurveyQuestions(ids, isFirstPageRandomized));
		setIsSorting(false);
	};

	const handleUpdateBeforeSortingStart = () => setIsSorting(true);

	const handleValidation = () => {
		if (hasUnsavedChanges) {
			setIsWarningModalOpen(true);
			return;
		}

		handleWarningModalSubmit();
	};

	const handleJumpToStepClick = (step: SurveyStep) => {
		dispatch(SurveyBuilderActionCreators.Jump(step));
	};

	return {
		availableCultures,
		questions,
		hasQuestions: !!surveyEditModel.questions.length,
		hasUnsavedChanges,
		isExpanded,
		isLoading,
		isSorting,
		isWarningModalOpen,
		pageFlowElement,
		surveyEditModel,
		handleEditPageFlowElement: setPageFlowElement,
		handleExpandCollapse: setIsExpanded,
		handleJumpToStepClick,
		handlePageFlowElementModalClose,
		handleSortingChange,
		handleUpdateBeforeSortingStart,
		handleValidation,
		handleWarningModalClose,
		handleWarningModalSubmit,
	};
};
